import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Loader2 } from "lucide-react";

const PAGE = 12;

export default function DestinationsHub() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [zone, setZone] = useState(null);
  const [travelType, setTravelType] = useState(null);
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    api.get("/public/destinations")
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("error"));
  }, []);

  const filtered = useMemo(() => {
    const items = data?.items || [];
    return items.filter((i) =>
      (!zone || i.zone === zone) &&
      (!travelType || (i.types_voyage || []).includes(travelType))
    );
  }, [data, zone, travelType]);

  useEffect(() => { setVisible(PAGE); }, [zone, travelType]);

  const shown = filtered.slice(0, visible);
  const canonical = (typeof window !== "undefined" ? window.location.origin : "") + "/destinations";

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1B18]">
      <Helmet>
        <html lang="fr" />
        <title>Toutes nos destinations de voyage sur mesure | DealTravel</title>
        <meta name="description" content="Explorez toutes nos destinations de voyage sur mesure : océan Indien, Asie, Caraïbes, Pacifique Sud… Filtrez par région et type de voyage et composez votre séjour d'exception avec DealTravel." />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DealTravel" />
        <meta property="og:title" content="Toutes nos destinations | DealTravel" />
        <meta property="og:description" content="Le catalogue complet de nos destinations de voyage sur mesure." />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Nav />

      <section className="pt-36 md:pt-44 pb-10 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <p className="label-eyebrow">Le catalogue DealTravel</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] mt-4">Toutes nos destinations.</h1>
          <p className="mt-5 text-base md:text-lg text-[#5c574f] max-w-[62ch] leading-relaxed">
            Des lagons de l'océan Indien aux archipels du Pacifique Sud, explorez nos destinations et composez le voyage qui vous ressemble.
          </p>
        </div>
      </section>

      {state === "loading" && (
        <div className="py-24 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#96836B]" /></div>
      )}
      {state === "error" && (
        <div className="py-24 text-center px-6"><p className="text-[#737373]">Une erreur est survenue. Merci de réessayer.</p></div>
      )}

      {state === "ok" && (
        <>
          {/* FILTER BAR */}
          {(data?.zones?.length > 0 || data?.travel_types?.length > 0) && (
            <section className="px-6 md:px-12 pb-4" data-testid="hub-filters">
              <div className="max-w-[1400px] mx-auto space-y-5 border-y border-[#EAE8E3] py-7">
                {data.zones.length > 0 && (
                  <FilterRow label="Région" options={data.zones} active={zone} onChange={setZone} testid="filter-zone" />
                )}
                {data.travel_types.length > 0 && (
                  <FilterRow label="Type de voyage" options={data.travel_types} active={travelType} onChange={setTravelType} testid="filter-type" />
                )}
              </div>
            </section>
          )}

          {/* GRID — identical card design to the homepage showcase */}
          <section className="py-12 md:py-16 px-6 md:px-12">
            <div className="max-w-[1400px] mx-auto">
              {filtered.length === 0 ? (
                <div className="py-24 text-center">
                  <p className="font-serif-display text-2xl">Aucune destination ne correspond</p>
                  <p className="text-[#737373] mt-2 text-sm">Modifiez vos filtres pour élargir la recherche.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="hub-grid">
                    {shown.map((d) => (
                      <Link key={d.id} to={d.url} data-testid={`hub-card-${d.slug}`} className="group cursor-pointer block">
                        <div className="aspect-[4/5] overflow-hidden bg-[#E4DCCF]">
                          {d.cover_image
                            ? <img src={d.cover_image} alt={d.cover_alt || d.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            : <div className="h-full w-full bg-[#E4DCCF]" />}
                        </div>
                        <div className="mt-5 flex items-baseline justify-between gap-3">
                          <h3 className="font-serif-display text-3xl leading-tight min-w-0">{d.title}</h3>
                          {d.subtitle && <p className="text-xs uppercase tracking-[0.18em] text-[#96836B] text-right shrink-0 max-w-[40%] truncate">{d.subtitle}</p>}
                        </div>
                      </Link>
                    ))}
                  </div>

                  {visible < filtered.length && (
                    <div className="mt-16 flex justify-center">
                      <button
                        onClick={() => setVisible((v) => v + PAGE)}
                        data-testid="hub-load-more"
                        className="text-[11px] uppercase tracking-[0.24em] border border-[#1A1B18] px-8 py-4 hover:bg-[#1A1B18] hover:text-[#FAF9F6] transition-colors"
                      >
                        Charger plus ({filtered.length - visible})
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}

const FilterRow = ({ label, options, active, onChange, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
    <span className="label-eyebrow shrink-0 sm:w-40">{label}</span>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      <Chip active={!active} onClick={() => onChange(null)}>Toutes</Chip>
      {options.map((o) => (
        <Chip key={o} active={active === o} onClick={() => onChange(active === o ? null : o)}>{o}</Chip>
      ))}
    </div>
  </div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`text-[11px] uppercase tracking-[0.16em] px-4 py-2 border transition-colors ${active ? "bg-[#1A1B18] text-white border-[#1A1B18]" : "border-[#D9D5CC] text-[#737373] hover:border-[#1A1B18] hover:text-[#1A1B18]"}`}
  >
    {children}
  </button>
);
